import React from 'react';
import { Search, Plus, X, ChevronDown } from 'lucide-react';
import { fieldStyles } from './ERPStyles';

// Reusable Input with Label
export const ERPInput = ({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  required = false,
  disabled = false,
  error,
  className = '',
  isDark = false,
  onLookup,
  onAdd,
  suffix,
  prefix,
  ...props
}) => {
  const baseStyle = `${fieldStyles.base} ${isDark ? fieldStyles.dark : fieldStyles.light}`;
  const stateStyle = error ? fieldStyles.error : required ? 'border-amber-400/60' : '';
  const disabledStyle = disabled ? fieldStyles.disabled : '';
  
  return (
    <div className={`space-y-1 ${className}`}>
      {label && (
        <label className={`text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
          {label}
          {required && <span className="text-amber-500">*</span>}
        </label>
      )}
      <div className="relative flex items-center gap-1">
        {prefix && (
          <span className={`text-xs font-medium px-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{prefix}</span>
        )}
        <input
          type={type}
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          className={`${baseStyle} ${stateStyle} ${disabledStyle} ${(onLookup || onAdd) ? 'pr-16' : ''}`}
          {...props}
        />
        {(onLookup || onAdd) && (
          <div className="absolute right-1 flex items-center gap-0.5">
            {onLookup && (
              <button
                type="button"
                onClick={onLookup}
                className={`p-1.5 rounded-md transition-colors ${isDark ? 'hover:bg-slate-700 text-blue-400' : 'hover:bg-slate-100 text-blue-600'}`}
                title="Search/Lookup"
              >
                <Search size={14} />
              </button>
            )}
            {onAdd && (
              <button
                type="button"
                onClick={onAdd}
                className={`p-1.5 rounded-md transition-colors ${isDark ? 'hover:bg-slate-700 text-emerald-400' : 'hover:bg-slate-100 text-emerald-600'}`}
                title="Add New"
              >
                <Plus size={14} />
              </button>
            )}
          </div>
        )}
        {suffix && (
          <span className={`text-xs font-medium px-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{suffix}</span>
        )}
      </div>
      {error && <p className="text-[10px] text-red-500 font-medium">{error}</p>}
    </div>
  );
};

// Dropdown Select
export const ERPSelect = ({
  label,
  value,
  onChange,
  options = [],
  placeholder = 'Select...',
  required = false,
  disabled = false,
  error,
  className = '',
  isDark = false,
  onAdd,
  ...props
}) => {
  const baseStyle = `${fieldStyles.base} ${isDark ? fieldStyles.dark : fieldStyles.light} appearance-none cursor-pointer`;
  const stateStyle = error ? fieldStyles.error : required ? 'border-amber-400/60' : '';
  const disabledStyle = disabled ? fieldStyles.disabled : '';
  
  return (
    <div className={`space-y-1 ${className}`}>
      {label && (
        <label className={`text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
          {label}
          {required && <span className="text-amber-500">*</span>}
        </label>
      )}
      <div className="relative flex items-center gap-1">
        <select
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          disabled={disabled}
          className={`${baseStyle} ${stateStyle} ${disabledStyle} pr-10`}
          {...props}
        >
          <option value="">{placeholder}</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <ChevronDown size={14} className={`absolute right-3 pointer-events-none ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
        {onAdd && (
          <button
            type="button"
            onClick={onAdd}
            className={`p-1.5 rounded-md transition-colors ${isDark ? 'hover:bg-slate-700 text-emerald-400' : 'hover:bg-slate-100 text-emerald-600'}`}
            title="Add New"
          >
            <Plus size={14} />
          </button>
        )}
      </div>
      {error && <p className="text-[10px] text-red-500 font-medium">{error}</p>}
    </div>
  );
};

// Checkbox
export const ERPCheckbox = ({
  label,
  checked,
  onChange,
  disabled = false,
  className = '',
  isDark = false,
}) => {
  return (
    <label className={`flex items-center gap-2 cursor-pointer group ${className} ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}>
      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all ${
        checked 
          ? 'bg-blue-600 border-blue-600' 
          : isDark ? 'border-slate-500 group-hover:border-slate-400' : 'border-slate-400 group-hover:border-slate-500'
      }`}>
        {checked && (
          <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={4}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        )}
      </div>
      <span className={`text-xs font-medium ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{label}</span>
    </label>
  );
};

// Radio Button Group
export const ERPRadioGroup = ({
  label,
  value,
  onChange,
  options = [],
  disabled = false,
  className = '',
  isDark = false,
  inline = true,
}) => {
  return (
    <div className={`space-y-1.5 ${className}`}>
      {label && (
        <label className={`text-[10px] font-bold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
          {label}
        </label>
      )}
      <div className={`flex ${inline ? 'flex-row gap-4' : 'flex-col gap-2'}`}>
        {options.map((opt) => (
          <label key={opt.value} className={`flex items-center gap-2 cursor-pointer group ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}>
            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all ${
              value === opt.value 
                ? 'border-blue-600' 
                : isDark ? 'border-slate-500 group-hover:border-slate-400' : 'border-slate-400 group-hover:border-slate-500'
            }`}>
              {value === opt.value && <div className="w-2 h-2 rounded-full bg-blue-600" />}
            </div>
            <span className={`text-xs font-medium ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{opt.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

// Section Card
export const ERPSection = ({
  title,
  icon: Icon,
  children,
  className = '',
  isDark = false,
  headerColor = 'bg-blue-600',
  bgColor,
  borderColor,
  collapsible = false,
  defaultCollapsed = false,
  badge,
  actions,
}) => {
  const [collapsed, setCollapsed] = React.useState(defaultCollapsed);
  
  const bg = bgColor || (isDark ? 'bg-slate-800/50' : 'bg-white');
  const border = borderColor || (isDark ? 'border-slate-700' : 'border-slate-200');
  
  return (
    <div className={`rounded-xl border shadow-sm overflow-hidden ${bg} ${border} ${className}`}>
      <div 
        className={`px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-white flex items-center justify-between ${headerColor} ${collapsible ? 'cursor-pointer' : ''}`}
        onClick={() => collapsible && setCollapsed(!collapsed)}
      >
        <div className="flex items-center gap-2">
          {Icon && <Icon size={14} />}
          <span>{title}</span>
          {badge && (
            <span className="px-2 py-0.5 rounded-full bg-white/20 text-[10px] font-bold">{badge}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {actions}
          {collapsible && (
            <ChevronDown size={14} className={`transition-transform ${collapsed ? '-rotate-90' : ''}`} />
          )}
        </div>
      </div>
      {!collapsed && (
        <div className="p-4">
          {children}
        </div>
      )}
    </div>
  );
};

// Numeric Input with formatting
export const ERPNumericInput = ({
  label,
  value,
  onChange,
  min,
  max,
  step = 1,
  precision = 2,
  required = false,
  disabled = false,
  error,
  className = '',
  isDark = false,
  prefix,
  suffix,
  highlight,
  ...props
}) => {
  const formatValue = (val) => {
    if (val === '' || val === null || val === undefined) return '';
    const num = parseFloat(val);
    if (isNaN(num)) return '';
    return num.toFixed(precision);
  };
  
  const baseStyle = `${fieldStyles.base} ${isDark ? fieldStyles.dark : fieldStyles.light} text-right font-mono`;
  const stateStyle = error ? fieldStyles.error : required ? 'border-amber-400/60' : '';
  const disabledStyle = disabled ? fieldStyles.disabled : '';
  const highlightStyle = highlight ? (isDark ? 'bg-yellow-900/30 border-yellow-600' : 'bg-yellow-100 border-yellow-400') : '';
  
  return (
    <div className={`space-y-1 ${className}`}>
      {label && (
        <label className={`text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
          {label}
          {required && <span className="text-amber-500">*</span>}
        </label>
      )}
      <div className="relative flex items-center">
        {prefix && (
          <span className={`text-xs font-medium px-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{prefix}</span>
        )}
        <input
          type="number"
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          className={`${baseStyle} ${stateStyle} ${disabledStyle} ${highlightStyle}`}
          {...props}
        />
        {suffix && (
          <span className={`absolute right-3 text-xs font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{suffix}</span>
        )}
      </div>
      {error && <p className="text-[10px] text-red-500 font-medium">{error}</p>}
    </div>
  );
};

// Display Field (Read-only)
export const ERPDisplayField = ({
  label,
  value,
  className = '',
  isDark = false,
  highlight,
  highlightColor = 'blue',
  prefix,
  suffix,
}) => {
  const highlightStyles = {
    blue: isDark ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100 text-blue-700',
    green: isDark ? 'bg-emerald-900/30 text-emerald-400' : 'bg-emerald-100 text-emerald-700',
    amber: isDark ? 'bg-amber-900/30 text-amber-400' : 'bg-amber-100 text-amber-700',
    red: isDark ? 'bg-red-900/30 text-red-400' : 'bg-red-100 text-red-700',
  };
  
  return (
    <div className={`space-y-1 ${className}`}>
      {label && (
        <label className={`text-[10px] font-bold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
          {label}
        </label>
      )}
      <div className={`px-3 py-2 text-sm rounded-lg font-mono ${
        highlight 
          ? highlightStyles[highlightColor] 
          : isDark ? 'bg-slate-800 text-slate-300' : 'bg-slate-100 text-slate-700'
      }`}>
        {prefix}{value}{suffix}
      </div>
    </div>
  );
};

// Modal/Drawer for Master Creation
export const ERPModal = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  isDark = false,
}) => {
  if (!isOpen) return null;
  
  const sizes = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative w-full ${sizes[size]} rounded-xl shadow-2xl ${isDark ? 'bg-slate-900 border border-slate-700' : 'bg-white'}`}>
        <div className={`flex items-center justify-between px-4 py-3 border-b ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
          <h3 className={`text-sm font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{title}</h3>
          <button
            onClick={onClose}
            className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-slate-800 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`}
          >
            <X size={16} />
          </button>
        </div>
        <div className="p-4 max-h-[70vh] overflow-y-auto">
          {children}
        </div>
      </div>
    </div>
  );
};

// Button
export const ERPButton = ({
  children,
  onClick,
  variant = 'primary',
  size = 'md',
  disabled = false,
  icon: Icon,
  className = '',
  isDark = false,
  ...props
}) => {
  const variants = {
    primary: 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-600/20',
    secondary: isDark ? 'bg-slate-700 hover:bg-slate-600 text-white' : 'bg-slate-200 hover:bg-slate-300 text-slate-700',
    success: 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-600/20',
    danger: 'bg-red-600 hover:bg-red-700 text-white shadow-lg shadow-red-600/20',
    warning: 'bg-amber-500 hover:bg-amber-600 text-white shadow-lg shadow-amber-500/20',
    ghost: isDark ? 'hover:bg-slate-800 text-slate-400' : 'hover:bg-slate-100 text-slate-600',
  };
  
  const sizes = {
    sm: 'px-2.5 py-1.5 text-[10px]',
    md: 'px-4 py-2 text-xs',
    lg: 'px-5 py-2.5 text-sm',
  };
  
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center justify-center gap-1.5 font-bold uppercase tracking-wide rounded-lg transition-all ${variants[variant]} ${sizes[size]} ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
      {...props}
    >
      {Icon && <Icon size={size === 'sm' ? 12 : size === 'lg' ? 16 : 14} />}
      {children}
    </button>
  );
};
